import { CirclePause, CirclePlay, Play, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { RunCountersLine, RunStatusBadge } from '../components/imports';
import { btnGhost, btnSecondary } from '../components/ui';
import { ApiError, type ImportRun, type ImportSource } from '../lib/api';
import { formatStamp } from '../lib/format';
import { useSession } from '../lib/session';

const ADAPTERS: Record<string, string> = {
  quisalento_pdf: 'Rivista in PDF',
  jsonld: 'Sito (JSON-LD)',
  ics_rss: 'Calendario ICS/RSS',
  css_selectors: 'Sito (selettori)',
  llm_generic: 'Sito (AI)',
  telegram_channel: 'Canale Telegram',
  email_inbox: 'Casella email',
  viaggiareinpuglia: 'Viaggiare in Puglia (API)',
  pugliaculture: 'Puglia Culture (circuito teatrale)',
  vivaticket: 'Vivaticket (biglietteria)',
  eventiitaliani: 'eventi-italiani.it',
  wp_tribe: 'Sito WordPress (The Events Calendar)',
  submission: 'Segnalazioni dal sito',
};

/** "cron(30 6 * * ? *)" → "ogni giorno alle 6:30" (le forme semplici; le altre restano com'è). */
function scheduleLabel(expr?: string): string {
  if (!expr) return 'solo a mano';
  const m = /^cron\((\d+) (\d+) \* \* \? \*\)$/.exec(expr);
  if (m) return `ogni giorno alle ${m[2]}:${m[1]!.padStart(2, '0')}`;
  const h = /^cron\((\d+) \* \* \* \? \*\)$/.exec(expr);
  if (h) return `ogni ora al minuto ${h[1]}`;
  return expr;
}

const OUTCOMES: Record<string, string> = {
  new: 'nuovi',
  duplicate: 'duplicati',
  update: 'aggiornamenti',
  invalid: 'non validi',
};

/** Soglia per accendere la pubblicazione automatica (docs/04-ingestione.md §2.8). */
const AUTO_PUBLISH_MIN = 0.97;
/** Sotto questo numero di decisioni la percentuale dice poco. */
const MIN_SAMPLE = 50;

const pct = (a: number, t: number) => `${Math.floor((a / t) * 1000) / 10}%`.replace('.', ',');

/** Quante volte la revisione ha confermato l'azione suggerita dalla pipeline. */
function AccuracyLine({ source }: { source: ImportSource }) {
  const a = source.accuracy;
  if (!a || a.total + a.uncertain === 0)
    return <p className="text-xs text-muted">Suggerimenti confermati: nessuna decisione ancora</p>;
  const ratio = a.total ? a.agreed / a.total : 0;
  const ready = a.total >= MIN_SAMPLE && ratio >= AUTO_PUBLISH_MIN;
  const detail = Object.entries(a.byOutcome)
    .map(([o, v]) => `${OUTCOMES[o] ?? o}: ${v!.agreed}/${v!.total}`)
    .concat(a.uncertain ? [`incerti decisi a mano: ${a.uncertain}`] : [])
    .join(' · ');
  return (
    <div className="text-sm">
      <p>
        Suggerimenti confermati:{' '}
        <strong className="font-medium">{a.total ? pct(a.agreed, a.total) : '—'}</strong>{' '}
        <span className="text-muted">
          ({a.agreed} su {a.total})
        </span>
        {!source.autoPublish && (
          <span
            className={`ml-2 px-1.5 py-0.5 text-[11px] font-medium uppercase ${ready ? 'bg-accent-soft' : 'bg-surface text-muted'}`}
            title={`Pubblicazione automatica possibile da ${pct(AUTO_PUBLISH_MIN, 1)} su almeno ${MIN_SAMPLE} decisioni`}
          >
            {ready ? 'Pronta per l’automatica' : 'Ancora in prova'}
          </span>
        )}
      </p>
      <p className="text-xs text-muted">{detail}</p>
    </div>
  );
}

/**
 * Sorgenti degli import: stato, ultima esecuzione, "Esegui ora" e pausa; sotto, le esecuzioni
 * recenti di tutte le sorgenti. La configurazione (adapter, frequenza) sta nel codice
 * (sse-ingestion/sources/*.yaml): qui si controlla, non si configura.
 */
export function Sources() {
  const { api, canEdit } = useSession();
  const [sources, setSources] = useState<ImportSource[] | null>(null);
  const [runs, setRuns] = useState<ImportRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [s, r] = await Promise.all([
        api<{ sources: ImportSource[] }>('/imports/sources'),
        api<{ runs: ImportRun[] }>('/imports/runs?limit=30'),
      ]);
      setSources(s.sources);
      setRuns(r.runs);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    }
  };

  useEffect(() => {
    if (canEdit) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  // un'esecuzione in corso si aggiorna da sola
  const running = runs.some((r) => r.status === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void load(), 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const runNow = async (s: ImportSource) => {
    setBusy(s.id);
    setNotice(null);
    try {
      await api(`/imports/sources/${s.id}/run`, { method: 'POST', body: {} });
      setNotice(`${s.name}: esecuzione avviata. I risultati compaiono qui sotto in pochi minuti.`);
      setTimeout(() => void load(), 4000);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(null);
    }
  };

  const togglePause = async (s: ImportSource) => {
    setBusy(s.id);
    try {
      await api(`/imports/sources/${s.id}/pause`, { method: 'POST', body: { paused: !s.paused } });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(null);
    }
  };

  const name = (id: string) => sources?.find((s) => s.id === id)?.name ?? id;

  return (
    <main className="mx-auto max-w-[1400px] p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-h2">Sorgenti</h1>
        <button onClick={() => void load()} className={btnGhost}>
          <RefreshCw className="size-4" aria-hidden /> Aggiorna
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-4 bg-danger-soft p-3 text-sm">
          Errore: {error}
        </p>
      )}
      {notice && (
        <p role="status" className="mt-4 bg-surface p-3 text-sm">
          {notice}
        </p>
      )}

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {sources?.length === 0 && (
          <p className="text-sm text-muted">Nessuna sorgente nella release in questo ambiente.</p>
        )}
        {sources?.map((s) => (
          <section key={s.id} className="flex flex-col gap-3 border border-surface-strong p-4">
            <header className="flex items-start justify-between gap-2">
              <div>
                <h2 className="font-medium">{s.name}</h2>
                <p className="text-xs text-muted">
                  {ADAPTERS[s.adapter] ?? s.adapter} · {scheduleLabel(s.schedule)} · affidabilità{' '}
                  {s.trust}
                </p>
              </div>
              {s.paused ? (
                <span className="bg-danger-soft px-2 py-0.5 text-[11px] font-medium uppercase">
                  In pausa
                </span>
              ) : !s.enabled ? (
                <span className="bg-surface px-2 py-0.5 text-[11px] font-medium uppercase text-muted">
                  Disattivata
                </span>
              ) : (
                <span
                  className="bg-surface-strong px-2 py-0.5 text-[11px] font-medium uppercase"
                  title={
                    s.autoPublish
                      ? 'Pubblica da sola i casi sicuri'
                      : 'Modalità ombra: tutto passa dalla revisione'
                  }
                >
                  {s.autoPublish ? 'Automatica' : 'In revisione'}
                </span>
              )}
            </header>
            <div className="text-sm">
              {s.lastRun ? (
                <>
                  <div className="flex items-center gap-2">
                    <RunStatusBadge status={s.lastRun.status} />
                    <Link to={`/sources/runs/${s.lastRun.id}`} className="hover:underline">
                      {formatStamp(s.lastRun.startedAt)}
                    </Link>
                  </div>
                  <RunCountersLine run={s.lastRun} />
                </>
              ) : (
                <span className="text-muted">Mai eseguita</span>
              )}
            </div>
            <AccuracyLine source={s} />
            {canEdit && (
              <div className="mt-auto flex flex-wrap gap-2">
                <button
                  onClick={() => void runNow(s)}
                  disabled={busy === s.id || !s.enabled}
                  className={btnSecondary}
                >
                  <Play className="size-4" aria-hidden /> Esegui ora
                </button>
                <button
                  onClick={() => void togglePause(s)}
                  disabled={busy === s.id}
                  className={btnGhost}
                  title={
                    s.paused
                      ? 'Riprende le esecuzioni pianificate'
                      : 'Sospende le esecuzioni pianificate (non quelle avviate a mano)'
                  }
                >
                  {s.paused ? (
                    <>
                      <CirclePlay className="size-4" aria-hidden /> Riprendi
                    </>
                  ) : (
                    <>
                      <CirclePause className="size-4" aria-hidden /> Metti in pausa
                    </>
                  )}
                </button>
              </div>
            )}
          </section>
        ))}
      </div>

      <h2 className="mt-8 text-label font-medium uppercase tracking-[0.06em]">
        Esecuzioni recenti
      </h2>
      <div className="relative mt-2 overflow-x-auto">
        <table className="w-full border-collapse text-sm md:min-w-[760px]">
          <thead>
            <tr className="border-b-2 border-fg text-left text-label uppercase tracking-[0.04em] text-muted">
              <th className="py-2 pr-3 font-medium">Avvio</th>
              <th className="py-2 pr-3 font-medium">Sorgente</th>
              <th className="py-2 pr-3 font-medium">Stato</th>
              <th className="py-2 pr-3 font-medium">Risultati</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-surface-strong align-top hover:bg-surface">
                <td className="py-2 pr-3 whitespace-nowrap">
                  <Link to={`/sources/runs/${r.id}`} className="font-medium hover:underline">
                    {formatStamp(r.startedAt)}
                  </Link>
                  <div className="text-xs text-muted">
                    {r.trigger === 'manual' ? `a mano${r.by ? ` (${r.by})` : ''}` : 'pianificata'}
                  </div>
                </td>
                <td className="py-2 pr-3">{name(r.sourceId)}</td>
                <td className="py-2 pr-3">
                  <RunStatusBadge status={r.status} />
                </td>
                <td className="py-2 pr-3">
                  <RunCountersLine run={r} />
                </td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={4} className="py-4 text-muted">
                  Nessuna esecuzione.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}
